import { type FC, Fragment, Suspense, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { MetaAdsCampaignReportItem } from "shared";
import { Icon } from "../../common/Icon";
import { Card } from "../../components/Card";
import { LinkGen } from "../../LinkGen";
import { useMetaAdsCampaigns } from "../useMetaAdsCampaigns";
import { useMetaAdsStatusUpdate } from "../useMetaAdsStatusUpdate";
import { AdSetTable } from "./AdSetTable";
import { MetricsColumns, MetricsHeaders } from "./MetricsColumns";
import { StatusToggle } from "./StatusToggle";

type SortKey =
	| "name"
	| "status"
	| "spend"
	| "impressions"
	| "clicks"
	| "ctr"
	| "conversions";

const CampaignTableInner: FC<{
	from: string;
	to: string;
	showStatusToggle: boolean;
}> = ({ from, to, showStatusToggle }) => {
	const navigate = useNavigate();
	const { data: campaigns } = useMetaAdsCampaigns(from, to);
	const { updateCampaignStatus } = useMetaAdsStatusUpdate();
	const [expandedCampaigns, setExpandedCampaigns] = useState<Set<string>>(
		new Set(),
	);
	const [sortKey, setSortKey] = useState<SortKey>("spend");
	const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

	const toggleCampaign = (id: string) => {
		setExpandedCampaigns((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const handleSort = (key: SortKey) => {
		if (sortKey === key) {
			setSortDir((d) => (d === "asc" ? "desc" : "asc"));
		} else {
			setSortKey(key);
			setSortDir("desc");
		}
	};

	const sorted = useMemo(() => {
		return [...campaigns].sort((a, b) => {
			const dir = sortDir === "asc" ? 1 : -1;
			if (sortKey === "name") {
				return a.name.localeCompare(b.name) * dir;
			}
			if (sortKey === "status") {
				return a.effectiveStatus.localeCompare(b.effectiveStatus) * dir;
			}
			return ((a[sortKey] as number) - (b[sortKey] as number)) * dir;
		});
	}, [campaigns, sortKey, sortDir]);

	const sortIcon = (key: SortKey) => {
		if (sortKey !== key) return null;
		return (
			<Icon
				name={sortDir === "asc" ? "arrow_upward" : "arrow_downward"}
				size={14}
			/>
		);
	};

	return (
		<Card>
			<Card.Header>Campaigns</Card.Header>
			<Card.Body style={{ padding: 0, overflowX: "auto" }}>
				<table className="table" style={{ marginBottom: 0 }}>
					<thead>
						<tr>
							<th
								style={{ cursor: "pointer" }}
								onClick={() => handleSort("name")}
							>
								Name {sortIcon("name")}
							</th>
							<th
								style={{ cursor: "pointer" }}
								onClick={() => handleSort("status")}
							>
								Status {sortIcon("status")}
							</th>
							{showStatusToggle && <th />}
							<MetricsHeaders />
						</tr>
					</thead>
					<tbody>
						{sorted.length === 0 && (
							<tr>
								<td
									colSpan={12}
									style={{ textAlign: "center" }}
									className="text-muted"
								>
									No campaign data for this date range.
								</td>
							</tr>
						)}
						{sorted.map((campaign: MetaAdsCampaignReportItem) => (
							<Fragment key={campaign.objectId}>
								<tr
									style={{ cursor: "pointer" }}
									onClick={() => toggleCampaign(campaign.objectId)}
									onKeyDown={(event) => {
										if (event.key === "Enter" || event.key === " ") {
											event.preventDefault();
											toggleCampaign(campaign.objectId);
										}
									}}
								>
									<td>
										<Icon
											name={
												expandedCampaigns.has(campaign.objectId)
													? "keyboard_arrow_down"
													: "chevron_right"
											}
											size={16}
										/>{" "}
										{campaign.name}
									</td>
									<td>
										<span
											className={`badge ${campaign.effectiveStatus === "ACTIVE" ? "bg-success" : campaign.effectiveStatus === "PAUSED" ? "bg-warning" : "bg-secondary"}`}
										>
											{campaign.effectiveStatus}
										</span>
										{showStatusToggle &&
											(campaign.effectiveStatus === "ACTIVE" ||
												campaign.effectiveStatus === "PAUSED") && (
												// biome-ignore lint/a11y/noStaticElementInteractions: propagation boundary only
												<span
													style={{ marginLeft: 8 }}
													onClick={(e) => e.stopPropagation()}
													onKeyDown={(e) => e.stopPropagation()}
												>
													<StatusToggle
														objectId={campaign.objectId}
														currentStatus={campaign.effectiveStatus}
														onToggle={(id, status) =>
															updateCampaignStatus.mutate({
																campaignId: id,
																status,
															})
														}
														isPending={updateCampaignStatus.isPending}
													/>
												</span>
											)}
									</td>
									{showStatusToggle && (
										<td
											onClick={(e) => e.stopPropagation()}
											onKeyDown={(e) => e.stopPropagation()}
										>
											<button
												type="button"
												className="btn btn-sm btn-outline-primary"
												onClick={() =>
													navigate(
														LinkGen.MetaAds.CampaignEdit(campaign.objectId),
													)
												}
											>
												Edit
											</button>
										</td>
									)}
									<MetricsColumns item={campaign} />
								</tr>
								{expandedCampaigns.has(campaign.objectId) && (
									<AdSetTable
										campaignId={campaign.objectId}
										from={from}
										to={to}
										showStatusToggle={showStatusToggle}
									/>
								)}
							</Fragment>
						))}
					</tbody>
				</table>
			</Card.Body>
		</Card>
	);
};

export const CampaignTable: FC<{
	from: string;
	to: string;
	showStatusToggle: boolean;
}> = (props) => (
	<Suspense fallback={<p>Loading campaigns...</p>}>
		<CampaignTableInner {...props} />
	</Suspense>
);
